/**
 * Crown3D — small rotating 3D crown rendered next to the leading team on
 * the TV scoreboard. Loads a GLB via @react-three/drei.
 *
 * Follows the same pattern as <Trophy3D /> (the gold cup used elsewhere on
 * the TV): transparent canvas, slow Y rotation, gentle floating tilt.
 *
 * Drop it inside any sized container — it fills 100% × 100% and the camera
 * is set so the model never clips the canvas edges as it rotates.
 */
/* eslint-disable react/no-unknown-property -- r3f uses lowercase JSX props */
import React, { Suspense, useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import Box from "@mui/material/Box";
import { Canvas, useFrame } from "@react-three/fiber";
import { useAnimations, useGLTF } from "@react-three/drei";
import crownGlb from "../../assets/models/crown-v2.glb";

function CrownMesh() {
  const groupRef = useRef();
  const { scene, animations } = useGLTF(crownGlb);

  // If the GLB ships with its own animation clip(s), play them. Otherwise
  // render the crown statically — no custom rotation from us.
  const { actions } = useAnimations(animations, groupRef);

  useEffect(() => {
    if (!actions) return;
    Object.values(actions).forEach((action) => {
      if (action) action.reset().play();
    });
  }, [actions]);

  const hasBakedAnim = Array.isArray(animations) && animations.length > 0;

  // The GLB is a metallic PBR crown. With no HDR environment, pure metal
  // reflects "nothing" and renders near-black. Ease the metalness/roughness and
  // lift envMap intensity so the gold base-colour texture reads under the lights.
  useEffect(() => {
    scene.traverse((o) => {
      if (o.isMesh && o.material) {
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        mats.forEach((m) => {
          if (m.metalness != null) m.metalness = Math.min(m.metalness, 0.45);
          if (m.roughness != null) m.roughness = Math.min(Math.max(m.roughness, 0.2), 0.5);
          m.envMapIntensity = 1.3;
          m.needsUpdate = true;
        });
      }
    });
  }, [scene]);

  // Auto-fit AND center — the fit MUST stay stable across mounts (the board
  // cycles scenes and re-mounts the crown). Two things keep it stable without
  // cloning (cloning shared the model's geometry, which got torn down on unmount
  // and left the crown blank on the next mount):
  //   1) reset the shared cached scene to an identity transform before measuring
  //      so a prior scale never contaminates the measurement;
  //   2) apply fitScale/offset to a WRAPPER group below, never to the scene
  //      itself, so the scene is never left scaled for the next measurement.
  const { fitScale, offset } = useMemo(() => {
    try {
      scene.position.set(0, 0, 0);
      scene.rotation.set(0, 0, 0);
      scene.scale.set(1, 1, 1);
      scene.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(scene);
      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      box.getSize(size);
      box.getCenter(center);
      const maxDim = Math.max(size.x, size.y, size.z) || 1;
      const s = 1.6 / maxDim;
      return { fitScale: s, offset: [-center.x * s, -center.y * s, -center.z * s] };
    } catch {
      return { fitScale: 1, offset: [0, 0, 0] };
    }
  }, [scene]);

  // When the GLB has no baked animation (this one doesn't), spin it ourselves —
  // a slow, smooth automatic Y rotation.
  useFrame((_, delta) => {
    if (groupRef.current && !hasBakedAnim) {
      groupRef.current.rotation.y += delta * 0.7;
    }
  });

  return (
    <group ref={groupRef}>
      <group scale={fitScale} position={offset}>
        <primitive object={scene} />
      </group>
    </group>
  );
}

export default function Crown3D({ size = "3.5vh" }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        pointerEvents: "none",
        // Slight glow under the crown to anchor it on the scoreboard.
        filter: "drop-shadow(0 2px 6px rgba(255, 200, 80, 0.35))",
      }}
    >
      <Canvas
        camera={{ position: [0, 0.2, 2.8], fov: 40 }}
        gl={{ alpha: true, antialias: true, preserveDrawingBuffer: false }}
        style={{ background: "transparent", overflow: "visible" }}
        dpr={[1, 2]}
      >
        {/* Key light — warm gold so the crown reads as polished metal. */}
        <directionalLight position={[3, 4, 3]} intensity={1.6} color="#FFE9B3" />
        {/* Fill light — cool from the opposite side to lift the shadowed face. */}
        <directionalLight position={[-3, 2, -1]} intensity={0.7} color="#9FB8FF" />
        {/* Soft ambient so the metallic surface never goes pitch black. */}
        <ambientLight intensity={0.7} />
        {/* Hemisphere fill — warm sky over cool ground so the gold texture reads
            evenly even without an HDR environment map. */}
        <hemisphereLight args={["#FFF3D6", "#22304A", 1.0]} />
        {/* Rim from above — sparkle highlights along the top edge. */}
        <pointLight position={[0, 3, 1]} intensity={1.4} color="#FFFFFF" />

        <Suspense fallback={null}>
          <CrownMesh />
        </Suspense>
      </Canvas>
    </Box>
  );
}

// Pre-cache the GLB so the first leader change doesn't show a flash of nothing.
useGLTF.preload(crownGlb);
